/**
 * The Company Details sub-pages, in one place.
 *
 * These used to be a Fluent `TabList` inside CompanyDetails.tsx. The mockup
 * moves that choice into the System Manager sidebar, so the same list now has
 * three consumers — the sidebar sub-menu, the router and the page itself — and
 * each of them needs the *same* labels, paths and visibility rules. Keeping one
 * literal means adding a sub-page is a single edit here plus its component.
 *
 * `hideKey` is deliberately the REACT_APP_HIDE_CTA_BUTTON key that already hid
 * the corresponding tab: moving a control from a tab strip into the navigation
 * should not silently un-hide it for deployments that had switched it off.
 */

export type CompanyDetailsSectionKey =
  | "basic-details"
  | "communication-credentials"
  | "frn-details";

export type CompanyDetailsSection = {
  key: CompanyDetailsSectionKey;
  label: string;
  hideKey: string;
};

export const COMPANY_DETAILS_SECTIONS: readonly CompanyDetailsSection[] = [
  {
    key: "basic-details",
    label: "Basic Details",
    hideKey: "sm_basicDetails",
  },
  {
    key: "communication-credentials",
    label: "Communication Credentials",
    hideKey: "sm_communication_credentials",
  },
  {
    key: "frn-details",
    label: "FRN Details",
    hideKey: "sm_frn_details",
  },
];

export const DEFAULT_COMPANY_DETAILS_SECTION: CompanyDetailsSectionKey =
  "basic-details";

const BASE_PATH = "/system-manager/company-details";

export const COMPANY_DETAILS_ROUTES = {
  base: BASE_PATH,
  section: (key: CompanyDetailsSectionKey) => `${BASE_PATH}/${key}`,
};

export const isCompanyDetailsSectionKey = (
  value: string | undefined
): value is CompanyDetailsSectionKey =>
  COMPANY_DETAILS_SECTIONS.some((section) => section.key === value);
